import {
  useState,
  useCallback,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Box, TextField, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useForm, Controller } from 'react-hook-form';
import {
  ModelDto,
  BuilderDto,
  SubdivisionDto,
  PhaseStructureTypeEnum,
} from '@vatos-pas/common';

import {
  Autocomplete,
  AsyncFieldWrapper,
  FormToolbar,
} from 'components/standalone';
import { useRefreshTrigger } from 'hooks';
import { createValidationResolver } from 'utils';
import { handleIntegerOnly } from 'utils/handleIntegerOnly';
import { validateModel } from '../views/modelValidation';
import {
  SwitchField,
  CostField,
  CostValue,
  EMPTY_COST,
} from './ModelFormFields';

export interface ModelFormValues {
  name: string;
  externalId: string;
  description: string;
  stockingSpec: string;
  subdivisionId: string;
  active: boolean;
  garageFirst: boolean;
  hasTrim: boolean;
  highCeiling: boolean;
  screwInspection: boolean;
  hasInteriorOptions: boolean;
  defaultInteriorOptionCost: CostValue;
  hasExteriorOptions: boolean;
  defaultExteriorOptionCost: CostValue;
  hasTrimOptions: boolean;
  defaultTrimOptionCost: CostValue;
  defaultTotalSquareFootage: string;
}

export interface ModelFormHandle {
  submit: () => void;
}

interface ModelFormProps {
  mode: 'create' | 'edit' | 'show';
  initialValues?: Partial<ModelDto>;
  onSubmit?: (values: ModelFormValues) => Promise<void>;
  loading: boolean;
  loadBuilders?: (search: string) => Promise<BuilderDto[]>;
  loadSubdivisions?: (
    builderId: string,
    search: string,
  ) => Promise<SubdivisionDto[]>;
  builderName?: string;
  builderLoading?: boolean;
  // Toolbar props
  onGoBack: () => void;
  onDelete?: () => void;
  showDelete?: boolean;
  deleteLoading?: boolean;
}

// Decimal columns can arrive as strings from the API, so normalize to a number.
const toCostValue = (value?: number | string | null): CostValue =>
  value === null || value === undefined || value === ''
    ? EMPTY_COST
    : Number(value);

const getDefaultValues = (
  initialValues?: Partial<ModelDto>,
): ModelFormValues => ({
  name: initialValues?.name || '',
  externalId: initialValues?.externalId || '',
  description: initialValues?.description || '',
  stockingSpec: initialValues?.stockingSpec || '',
  subdivisionId: initialValues?.subdivisionId || '',
  active: initialValues?.active ?? true,
  garageFirst: initialValues?.garageFirst ?? false,
  hasTrim: initialValues?.hasTrim ?? false,
  highCeiling: initialValues?.highCeiling ?? false,
  screwInspection: initialValues?.screwInspection ?? false,
  hasInteriorOptions: initialValues?.hasInteriorOptions ?? false,
  defaultInteriorOptionCost: toCostValue(
    initialValues?.defaultInteriorOptionCost,
  ),
  hasExteriorOptions: initialValues?.hasExteriorOptions ?? false,
  defaultExteriorOptionCost: toCostValue(
    initialValues?.defaultExteriorOptionCost,
  ),
  hasTrimOptions: initialValues?.hasTrimOptions ?? false,
  defaultTrimOptionCost: toCostValue(initialValues?.defaultTrimOptionCost),
  defaultTotalSquareFootage:
    initialValues?.defaultTotalSquareFootage?.toString() || '',
});

export const ModelForm = forwardRef<ModelFormHandle, ModelFormProps>(
  (
    {
      mode,
      initialValues,
      onSubmit,
      loading,
      loadBuilders,
      loadSubdivisions,
      builderName: initialBuilderName,
      builderLoading = false,
      onGoBack,
      onDelete,
      showDelete = false,
      deleteLoading = false,
    },
    ref,
  ) => {
    const classes = useStyles();
    const theme = useTheme();
    const isReadOnly = mode === 'show';
    const isCreateMode = mode === 'create';

    const formValues = useMemo(
      () => (initialValues ? getDefaultValues(initialValues) : undefined),
      [initialValues],
    );

    const {
      control,
      handleSubmit,
      watch,
      setValue,
      formState: { errors },
    } = useForm<ModelFormValues>({
      defaultValues: getDefaultValues(initialValues),
      values: formValues,
      resetOptions: { keepDirtyValues: true },
      resolver: createValidationResolver<ModelFormValues>(validateModel),
      mode: 'onBlur',
    });

    // Builder state (create mode only) - kept separate since it's async-loaded
    const [selectedBuilder, setSelectedBuilder] = useState<BuilderDto | null>(
      null,
    );
    const [subdivisionOptionsCache, setSubdivisionOptionsCache] = useState<
      SubdivisionDto[]
    >([]);
    const [refreshKey, setRefreshKey] = useState(0);

    // Re-fetch options when toolbar refresh is clicked
    useRefreshTrigger(() => {
      setRefreshKey(k => k + 1);
    });

    // Watch values for conditional rendering
    const hasInteriorOptions = watch('hasInteriorOptions');
    const hasExteriorOptions = watch('hasExteriorOptions');
    const hasTrim = watch('hasTrim');
    const hasTrimOptions = watch('hasTrimOptions');
    const subdivisionId = watch('subdivisionId');

    const getSubdivisionById = useCallback(
      (id: string): SubdivisionDto | null => {
        return subdivisionOptionsCache.find(s => s.id === id) || null;
      },
      [subdivisionOptionsCache],
    );

    const phaseStructureType = useMemo(() => {
      if (mode !== 'create' && initialValues?.subdivision) {
        return initialValues.subdivision.phaseStructureType;
      }
      const subdivision = getSubdivisionById(subdivisionId);
      return subdivision?.phaseStructureType;
    }, [mode, initialValues?.subdivision, subdivisionId, getSubdivisionById]);

    const isDrywallAndPaint =
      phaseStructureType === PhaseStructureTypeEnum.DrywallAndPaint;
    const showTrimField = isDrywallAndPaint;
    const showPaymentOptions = isDrywallAndPaint;

    const loadSubdivisionsForBuilder = useCallback(
      async (search: string): Promise<SubdivisionDto[]> => {
        if (!loadSubdivisions || !selectedBuilder?.id) return [];
        const options = await loadSubdivisions(selectedBuilder.id, search);
        setSubdivisionOptionsCache(options);
        return options;
      },
      [loadSubdivisions, selectedBuilder?.id],
    );

    const handleBuilderChange = useCallback(
      (newBuilder: BuilderDto | null) => {
        setSelectedBuilder(newBuilder);
        setSubdivisionOptionsCache([]);
        setValue('subdivisionId', '');
      },
      [setValue],
    );

    const onFormSubmit = useMemo(
      () =>
        handleSubmit(async data => {
          if (!onSubmit) return;
          await onSubmit(data);
        }),
      [handleSubmit, onSubmit],
    );

    useImperativeHandle(ref, () => ({ submit: onFormSubmit }), [onFormSubmit]);

    return (
      <Box>
        <Box className={classes.formContent}>
          <Box display="flex" alignItems="center" width="100%">
            <Box
              display="flex"
              width="100%"
              mb={2}
              ml={2}
              style={{ gap: theme.spacing(4) }}
            >
              {isCreateMode && loadBuilders ? (
                <>
                  <Autocomplete<BuilderDto>
                    key={`builder-${refreshKey}`}
                    label="Builder"
                    value={selectedBuilder}
                    onChange={handleBuilderChange}
                    loadOptions={loadBuilders}
                    getOptionLabel={option => option.name}
                    getOptionValue={option => option.id}
                    fullWidth
                  />

                  <Controller
                    name="subdivisionId"
                    control={control}
                    render={({ field, fieldState: { error } }) => {
                      const subdivisionObject = getSubdivisionById(field.value);
                      return (
                        <Autocomplete<SubdivisionDto>
                          key={`subdivision-${refreshKey}`}
                          loadTrigger="focus"
                          label="Subdivision"
                          value={subdivisionObject}
                          onChange={newValue =>
                            field.onChange(newValue?.id || '')
                          }
                          loadOptions={loadSubdivisionsForBuilder}
                          getOptionLabel={option => option.name}
                          getOptionValue={option => option.id}
                          disabled={!selectedBuilder}
                          error={error?.message}
                          fullWidth
                        />
                      );
                    }}
                  />
                </>
              ) : (
                <>
                  <AsyncFieldWrapper loading={builderLoading}>
                    <TextField
                      className={classes.input}
                      value={initialBuilderName || ''}
                      fullWidth
                      disabled
                      label="Builder"
                    />
                  </AsyncFieldWrapper>
                  <TextField
                    className={classes.input}
                    value={initialValues?.subdivision?.name || ''}
                    fullWidth
                    disabled
                    label="Subdivision"
                  />
                </>
              )}

              <Box display="flex" alignItems="center">
                <SwitchField
                  name="active"
                  control={control}
                  label="Active"
                  disabled={isReadOnly}
                />
              </Box>
            </Box>
          </Box>

          <Box className={classes.fieldsWithBoolean} mb={3}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  className={classes.halfWidth}
                  label="Name"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  margin="dense"
                  variant="filled"
                  disabled={isReadOnly}
                />
              )}
            />
            <Controller
              name="externalId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  className={classes.halfWidth}
                  label="External ID"
                  error={!!errors.externalId}
                  helperText={errors.externalId?.message}
                  margin="dense"
                  variant="filled"
                  disabled={isReadOnly}
                />
              )}
            />
          </Box>

          <Box className={classes.fieldsWithBoolean} mb={3}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  className={classes.halfWidth}
                  label="Description"
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  multiline
                  rows={3}
                  margin="dense"
                  variant="filled"
                  disabled={isReadOnly}
                />
              )}
            />
            <Controller
              name="stockingSpec"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  className={classes.halfWidth}
                  label="Stocking Spec"
                  error={!!errors.stockingSpec}
                  helperText={errors.stockingSpec?.message}
                  multiline
                  rows={3}
                  margin="dense"
                  variant="filled"
                  disabled={isReadOnly}
                />
              )}
            />
          </Box>

          <Box className={classes.fieldsWithBoolean} ml={2} mb={3}>
            <Controller
              name="defaultTotalSquareFootage"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <TextField
                  {...field}
                  className={classes.inputNumberCost}
                  label="Default Total Square Footage"
                  type="number"
                  value={value}
                  onChange={e =>
                    onChange(String(handleIntegerOnly(e.target.value) || ''))
                  }
                  margin="dense"
                  variant="filled"
                  disabled={isReadOnly}
                />
              )}
            />
          </Box>

          <Box ml={2}>
            <Typography className={classes.titleItem}>
              Additional Workflow Options
            </Typography>
          </Box>
          <Box
            className={classes.fieldsWithBoolean}
            display="flex"
            mx={2}
            mb={3}
          >
            <SwitchField
              name="garageFirst"
              control={control}
              label="Garage"
              className={classes.input}
              disabled={isReadOnly}
            />
            {showTrimField && (
              <SwitchField
                name="hasTrim"
                control={control}
                label="Trim"
                className={classes.booleanItem}
                disabled={isReadOnly}
                onUncheck={() => {
                  setValue('hasTrimOptions', false);
                  setValue('defaultTrimOptionCost', EMPTY_COST);
                }}
              />
            )}
          </Box>

          <Box ml={2}>
            <Typography className={classes.titleItem}>
              Additional Payment Options
            </Typography>
          </Box>
          <Box className={classes.fieldsWithBoolean} display="flex" mx={2}>
            <SwitchField
              name="highCeiling"
              control={control}
              label="High Ceiling"
              className={classes.input}
              disabled={isReadOnly}
            />
            <SwitchField
              name="screwInspection"
              control={control}
              label="Screw Inspection"
              className={classes.input}
              disabled={isReadOnly}
            />
            {showPaymentOptions && (
              <>
                <SwitchField
                  name="hasInteriorOptions"
                  control={control}
                  label="Paint Interior Options"
                  className={classes.input}
                  disabled={isReadOnly}
                  onUncheck={() =>
                    setValue('defaultInteriorOptionCost', EMPTY_COST)
                  }
                />
                {hasInteriorOptions && (
                  <CostField
                    name="defaultInteriorOptionCost"
                    control={control}
                    label="Default Interior Option Cost"
                    className={classes.inputNumberCost}
                    disabled={isReadOnly}
                  />
                )}
                <SwitchField
                  name="hasExteriorOptions"
                  control={control}
                  label="Paint Exterior Options"
                  className={classes.input}
                  disabled={isReadOnly}
                  onUncheck={() =>
                    setValue('defaultExteriorOptionCost', EMPTY_COST)
                  }
                />
                {hasExteriorOptions && (
                  <CostField
                    name="defaultExteriorOptionCost"
                    control={control}
                    label="Default Exterior Option Cost"
                    className={classes.inputNumberCost}
                    disabled={isReadOnly}
                  />
                )}
                <SwitchField
                  name="hasTrimOptions"
                  control={control}
                  label="Trim Option"
                  className={classes.input}
                  disabled={!hasTrim || isReadOnly}
                  onUncheck={() =>
                    setValue('defaultTrimOptionCost', EMPTY_COST)
                  }
                />
                <CostField
                  name="defaultTrimOptionCost"
                  control={control}
                  label="Default Trim Option Cost"
                  className={classes.inputNumberCost}
                  min={0.01}
                  error={errors.defaultTrimOptionCost?.message}
                  disabled={!hasTrimOptions || isReadOnly}
                />
              </>
            )}
          </Box>
        </Box>

        <FormToolbar
          onGoBack={onGoBack}
          onSave={onFormSubmit}
          onDelete={onDelete}
          saveLoading={loading}
          showSave={!isReadOnly}
          showDelete={showDelete}
          deleteLoading={deleteLoading}
        />
      </Box>
    );
  },
);

const useStyles = makeStyles(theme => ({
  formContent: {
    padding: theme.spacing(2),
  },
  input: {},
  fields: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  fieldsWithBoolean: {
    width: '100%',
    display: 'flex',
  },
  halfWidth: {
    width: '50%',
    margin: '0px 15px',
  },
  expand: {
    width: '100%',
  },
  titleItem: {
    fontWeight: 'bold',
    fontSize: 18,
  },
  inputNumberCost: {
    margin: '0px 15px 0px 0px',
    minWidth: '240px',
  },
  booleanItem: {
    margin: '0px 15px',
  },
}));

ModelForm.displayName = 'ModelForm';

export default ModelForm;
